import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "@/hooks/useUser";
import { useImpersonation } from "@/hooks/useImpersonation";
import {
  fetchDashboardOverview,
  fetchConversations,
  fetchVerificationStatus,
  fetchReviews,
  type DashboardOverview,
  type Conversation,
  type VerificationStatus,
  type PendingReview,
  type Review,
} from "@/lib/users-api";
import { listingsApi, type OwnerListing } from "@/lib/listings-api";
import { bookingsApi, type EarningsData, type TripListItem } from "@/lib/bookings-api";
import { subscriptionsApi, type OwnerSubscription } from "@/lib/subscriptions-api";
import { RateLimitError } from "@/lib/api-error";

export type BookingsTab = "pending" | "upcoming" | "past" | "in_progress";

/**
 * Task #4249 — the dashboard fires ~12 parallel reads on load. On a
 * reload/refresh burst this can trip the per-IP rate limiter and a
 * single idempotent GET comes back 429. Rather than degrading the whole
 * dashboard into its error state, retry that one read ONCE, respecting
 * the server's `Retry-After` hint. Scoped to 429 only — every other
 * error fails immediately (no blanket retry that would mask real bugs).
 *
 * Spread into each dashboard query option below so the policy travels
 * with the read and can never drift between sections.
 */
const RATE_LIMIT_RETRY = {
  retry: (failureCount: number, error: unknown) =>
    error instanceof RateLimitError && failureCount < 1,
  retryDelay: (_attempt: number, error: unknown) =>
    error instanceof RateLimitError
      ? Math.min(Math.max(error.retryAfter, 1), 60) * 1000
      : 0,
} as const;

export const DASHBOARD_BOOKING_TABS: BookingsTab[] = ["pending", "upcoming", "past", "in_progress"];

export interface OwnerDashboardStripeStatus {
  connected: boolean;
  accountId?: string | null;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
  /** True when the account record exists but onboarding is incomplete
   *  (charges/payouts disabled, details not submitted, or requirements
   *  past due). UI gates that show "Connect with Stripe" CTAs should
   *  treat `needsOnboarding === true` the same as `connected === false`. */
  needsOnboarding?: boolean;
  disabledReason?: string | null;
  /** Task #7311 — outstanding Stripe requirements so the UI can show
   *  targeted copy (e.g. "Accept Terms of Service") instead of the
   *  generic "finish onboarding" prompt. */
  currentlyDue?: string[];
  balance?: { available: number; pending: number; currency: string } | null;
  bankInfo?: { bankName: string | null; last4: string; currency: string } | null;
}

export type OwnerListingsResponse = { listings: OwnerListing[] };
export type OwnerSubscriptionResponse = { subscription: OwnerSubscription | null };

/**
 * Centralised TanStack Query keys for every read on the host/renter
 * dashboard. Keys are scoped by the *effective* user id (impersonation
 * target if active, otherwise the authenticated user id) so that an
 * admin who switches impersonation targets sees a cold cache for the
 * new user instead of stale data from the previous one.
 *
 * Use the prefixes (`all`, `byUser`, `bookingsAll`) with
 * `queryClient.invalidateQueries({ queryKey })` to invalidate broad
 * groups in one call. The leaf keys (`overview`, `bookings(tab)`,
 * etc.) are what `useQuery` should consume directly.
 */
export const dashboardKeys = {
  all: ["dashboard"] as const,
  byUser: (userId: string | undefined | null) =>
    [...dashboardKeys.all, userId ?? "anon"] as const,
  overview: (userId: string | undefined | null) =>
    [...dashboardKeys.byUser(userId), "overview"] as const,
  listings: (userId: string | undefined | null) =>
    [...dashboardKeys.byUser(userId), "listings"] as const,
  bookingsAll: (userId: string | undefined | null) =>
    [...dashboardKeys.byUser(userId), "bookings"] as const,
  bookings: (userId: string | undefined | null, tab: BookingsTab) =>
    [...dashboardKeys.bookingsAll(userId), tab] as const,
  earnings: (userId: string | undefined | null) =>
    [...dashboardKeys.byUser(userId), "earnings"] as const,
  stripeStatus: (userId: string | undefined | null) =>
    [...dashboardKeys.byUser(userId), "stripe-status"] as const,
  subscription: (userId: string | undefined | null) =>
    [...dashboardKeys.byUser(userId), "subscription"] as const,
  conversations: (userId: string | undefined | null) =>
    [...dashboardKeys.byUser(userId), "conversations"] as const,
  verification: (userId: string | undefined | null) =>
    [...dashboardKeys.byUser(userId), "verification"] as const,
  pendingReviews: (userId: string | undefined | null) =>
    [...dashboardKeys.byUser(userId), "pending-reviews"] as const,
};

/**
 * The cache-key user. When an admin is impersonating, the API calls
 * are made with the impersonation context so the data belongs to the
 * impersonated user — keying by their id keeps the two views isolated
 * in the client cache too.
 */
export function useDashboardUserId(): string | null {
  const { user } = useUser();
  const { effectiveUserId } = useImpersonation();
  return effectiveUserId ?? user?.id ?? null;
}

/**
 * Per-query option factories. Each one returns the `{ queryKey,
 * queryFn, enabled }` shape that `useQuery` expects so a consumer
 * never has to handcraft a key/fn pair (and we can never drift the
 * key from where the fetch happens).
 */
export const dashboardQueryOptions = {
  overview: (userId: string | null, token: string | undefined) => ({
    queryKey: dashboardKeys.overview(userId),
    queryFn: (): Promise<DashboardOverview> => fetchDashboardOverview(token!),
    enabled: !!token && !!userId,
    ...RATE_LIMIT_RETRY,
  }),
  listings: (userId: string | null, token: string | undefined) => ({
    queryKey: dashboardKeys.listings(userId),
    queryFn: (): Promise<OwnerListingsResponse> => listingsApi.getMyListings(token!),
    enabled: !!token && !!userId,
    ...RATE_LIMIT_RETRY,
  }),
  bookings: (userId: string | null, token: string | undefined, tab: BookingsTab) => ({
    queryKey: dashboardKeys.bookings(userId, tab),
    queryFn: async (): Promise<TripListItem[]> => {
      const bookings = await bookingsApi.getOwnerBookings(token!, tab);
      if (!Array.isArray(bookings)) throw new Error("Incomplete bookings response");
      return bookings;
    },
    enabled: !!token && !!userId,
    ...RATE_LIMIT_RETRY,
  }),
  earnings: (userId: string | null, token: string | undefined) => ({
    queryKey: dashboardKeys.earnings(userId),
    queryFn: (): Promise<EarningsData> => bookingsApi.getOwnerEarnings(token!),
    enabled: !!token && !!userId,
    ...RATE_LIMIT_RETRY,
  }),
  stripeStatus: (userId: string | null, token: string | undefined) => ({
    queryKey: dashboardKeys.stripeStatus(userId),
    queryFn: (): Promise<OwnerDashboardStripeStatus> =>
      bookingsApi.getStripeConnectStatus(token!),
    enabled: !!token && !!userId,
    ...RATE_LIMIT_RETRY,
  }),
  subscription: (userId: string | null, token: string | undefined) => ({
    queryKey: dashboardKeys.subscription(userId),
    queryFn: (): Promise<OwnerSubscriptionResponse> =>
      subscriptionsApi.getCurrentSubscription(token!),
    enabled: !!token && !!userId,
    ...RATE_LIMIT_RETRY,
  }),
  conversations: (userId: string | null, token: string | undefined) => ({
    queryKey: dashboardKeys.conversations(userId),
    queryFn: (): Promise<Conversation[]> => fetchConversations(token!),
    enabled: !!token && !!userId,
    ...RATE_LIMIT_RETRY,
  }),
  verification: (userId: string | null, token: string | undefined) => ({
    queryKey: dashboardKeys.verification(userId),
    queryFn: (): Promise<VerificationStatus | null> => fetchVerificationStatus(token!),
    enabled: !!token && !!userId,
    ...RATE_LIMIT_RETRY,
  }),
  pendingReviews: (userId: string | null, token: string | undefined) => ({
    queryKey: dashboardKeys.pendingReviews(userId),
    queryFn: (): Promise<Review[] | PendingReview[]> => fetchReviews(token!, "pending"),
    enabled: !!token && !!userId,
    ...RATE_LIMIT_RETRY,
  }),
};

/**
 * Imperative invalidation helpers, scoped to the active user. Pages
 * call these from mutation handlers (approve / decline / cancel /
 * pause / etc.) so the dashboard data refetches automatically on the
 * next render — replacing the bespoke `refreshX()` callbacks the old
 * `useOwnerDashboard` hook used to expose.
 */
export function useDashboardInvalidator() {
  const queryClient = useQueryClient();
  const userId = useDashboardUserId();
  const { session } = useUser();
  const token = session?.access_token;

  return useMemo(
    () => ({
      /** Invalidate every dashboard query for the active user. */
      invalidateAll: () =>
        queryClient.invalidateQueries({ queryKey: dashboardKeys.byUser(userId) }),
      /** Invalidate every bookings tab (`pending` / `upcoming` / `past`). */
      invalidateBookings: () =>
        queryClient.invalidateQueries({ queryKey: dashboardKeys.bookingsAll(userId) }),
      /** An action-owned snapshot: never reuse an in-flight pre-mutation read,
       * swallow refetch errors, or mistake a missing bucket for an empty one. */
      refreshBookings: async () => {
        if (!userId || !token) throw new Error("Booking session unavailable");
        const queryKey = dashboardKeys.bookingsAll(userId);
        await queryClient.cancelQueries({ queryKey });
        await queryClient.invalidateQueries({ queryKey, refetchType: "none" });
        const [pending, upcoming, past, inProgress] = await Promise.all(
          DASHBOARD_BOOKING_TABS.map(tab =>
            queryClient.fetchQuery({
              ...dashboardQueryOptions.bookings(userId, token, tab),
              staleTime: 0,
            }),
          ),
        );
        return { pending, upcoming, past, inProgress };
      },
      /** Invalidate a specific bookings tab. */
      invalidateBookingsTab: (tab: BookingsTab) =>
        queryClient.invalidateQueries({ queryKey: dashboardKeys.bookings(userId, tab) }),
      invalidateOverview: () =>
        queryClient.invalidateQueries({ queryKey: dashboardKeys.overview(userId) }),
      invalidateListings: () =>
        queryClient.invalidateQueries({ queryKey: dashboardKeys.listings(userId) }),
      invalidateEarnings: () =>
        queryClient.invalidateQueries({ queryKey: dashboardKeys.earnings(userId) }),
      invalidateStripeStatus: () =>
        queryClient.invalidateQueries({ queryKey: dashboardKeys.stripeStatus(userId) }),
      invalidateConversations: () =>
        queryClient.invalidateQueries({ queryKey: dashboardKeys.conversations(userId) }),
      invalidateVerification: () =>
        queryClient.invalidateQueries({ queryKey: dashboardKeys.verification(userId) }),
      invalidatePendingReviews: () =>
        queryClient.invalidateQueries({ queryKey: dashboardKeys.pendingReviews(userId) }),
    }),
    [queryClient, userId, token],
  );
}

/**
 * Optimistic listing updater for `MyListingsPage`. Wraps the
 * `setQueryData` call so callers can keep the familiar
 * `(prev: OwnerListing[]) => OwnerListing[]` signature without
 * leaking the `{ listings: [...] }` envelope shape.
 */
export function useOptimisticListings() {
  const queryClient = useQueryClient();
  const userId = useDashboardUserId();

  return useCallback(
    (
      updater:
        | OwnerListing[]
        | ((prev: OwnerListing[]) => OwnerListing[]),
    ) => {
      queryClient.setQueryData<OwnerListingsResponse>(
        dashboardKeys.listings(userId),
        (prev) => {
          const current = prev?.listings ?? [];
          const next =
            typeof updater === "function"
              ? (updater as (p: OwnerListing[]) => OwnerListing[])(current)
              : updater;
          return { listings: next };
        },
      );
    },
    [queryClient, userId],
  );
}
